import { useCallback, useEffect, useState } from "react";
import type { PerfumeRepositoryFirebase } from "@/repositories";
import type { Perfume, PerfumeIngredient } from "@/models";
import {
  allAvailableIngredients,
  isPerfumeEmpty,
  makeIngredient,
  removeEmptyComponents,
} from "@/models";
import { useSimpleLoader } from "@/hooks/SimpleLoader";
import { showLoader, useShowLoaderIf, type LoadingDispatcher } from "@/hooks/Loading";

export interface PerfumeRecipiesService {
  perfumeRepository: PerfumeRepositoryFirebase;
}

export function useTrackPerfume({
  service,
  loadingDispatcher,
  perfumeId,
}: {
  service: PerfumeRecipiesService;
  loadingDispatcher: LoadingDispatcher;
  perfumeId: string | null;
}) {
  const [perfume, setPerfume] = useState<Perfume | null>(null);

  useShowLoaderIf(loadingDispatcher, perfumeId === null);

  useEffect(() => {
    if (perfumeId === null) {
      return;
    }

    const unsubscribe = service.perfumeRepository.trackPerfumeById({
      recipeId: perfumeId,
      onChange: (newPerfume: Perfume | null) => {
        setPerfume(newPerfume);
      },
    });

    return unsubscribe;
  }, [perfumeId, service.perfumeRepository]);

  return {
    perfume,
  };
}

export async function deletePerfume(
  service: PerfumeRecipiesService,
  loadingDispatcher: LoadingDispatcher,
  recipeId: string,
) {
  await showLoader(loadingDispatcher, service.perfumeRepository.deletePerfume(recipeId));
}

export async function deleteAllEmptyPerfumeRecipies(
  service: PerfumeRecipiesService,
  loadingDispatcher: LoadingDispatcher,
) {
  const deleteRecipies = (async () => {
    const toDelete: string[] = [];
    let total = 0;
    for await (const perfume of service.perfumeRepository.listAllPerfumes()) {
      total++;
      if (isPerfumeEmpty(perfume)) {
        toDelete.push(perfume.recipeId);
      }
    }
    console.log(`Found ${total} perfume recipes, ${toDelete.length} empty`);
    for (const recipeId of toDelete) {
      await service.perfumeRepository.deletePerfume(recipeId);
    }
    console.log(`Deleted ${toDelete.length} empty perfume recipes`);
  })();

  await showLoader(loadingDispatcher, deleteRecipies);
}

export function useLastPerfumes(
  service: PerfumeRecipiesService,
  loadingDispatcher: LoadingDispatcher,
  numberOfLastPerfumes: number = 1e6,
) {
  const recipiesLoader = useCallback(async () => {
    return service.perfumeRepository.listPerfume(numberOfLastPerfumes);
  }, [service.perfumeRepository, numberOfLastPerfumes]);

  const [recipies, refreshRecipies] = useSimpleLoader<Perfume[]>(loadingDispatcher, recipiesLoader);

  return {
    recipies,
    refreshRecipies,
  };
}

// export function useSearchPerfumes(
//   service: PerfumeRecipiesService, // eslint-disable-line @typescript-eslint/no-unused-vars
//   loadingDispatcher: LoadingDispatcher, // eslint-disable-line @typescript-eslint/no-unused-vars
// ) {
//   const [searchResults] = useState<Perfume[] | null>();

//   const searchPerfumes = useCallback(
//     async (search: string) => {
//       const searchResult = await showLoader(
//         loadingDispatcher,
//         service.perfumeRepository.searchPerfumes(search),
//       );
//       setSearchResults(searchResult);
//     },
//     [service.perfumeRepository, loadingDispatcher],
//   );

//   const searchPerfumesDebounced = useMemo(
//     () =>
//       debounce((search) => {
//         searchPerfumes(search);
//       }, 1000),
//     [searchPerfumes],
//   );

//   const [search, setSearch] = useState("");

//   useEffect(() => searchPerfumesDebounced(search), [searchPerfumesDebounced, search]);

//   const searchPerfumesNow = useCallback(async () => {
//     searchPerfumesDebounced.clear();
//     await searchPerfumes(search);
//   }, []);

//   return {
//     search,
//     setSearch,
//     searchPerfumesNow,
//     searchResults,
//   };
// }

type IngredientInventory = {
  ingredient: PerfumeIngredient;
  volumeUsed: number;
};

export function useIngredientsInventory(
  service: PerfumeRecipiesService,
  loadingDispatcher: LoadingDispatcher,
) {
  const { recipies } = useLastPerfumes(service, loadingDispatcher);
  const [inventory, setInventory] = useState<IngredientInventory[] | null>(null);

  useEffect(() => {
    if (recipies === null) {
      return;
    }

    const inventoryMap: Map<string, IngredientInventory> = new Map();
    const getIngredientInventory = (ingredient: PerfumeIngredient): IngredientInventory => {
      let ingredientInventory = inventoryMap.get(ingredient.name);
      if (ingredientInventory === undefined) {
        ingredientInventory = {
          ingredient,
          volumeUsed: 0,
        };
        inventoryMap.set(ingredient.name, ingredientInventory);
      }

      return ingredientInventory;
    };

    for (const component of recipies.flatMap((recipe) =>
      removeEmptyComponents(recipe.components),
    )) {
      const [ingredient] = makeIngredient(component, allAvailableIngredients);
      if (ingredient !== null) {
        getIngredientInventory(ingredient).volumeUsed += component.volume;
      }
    }

    const sortedInventory = [...inventoryMap.values()].sort((a, b) => a.volumeUsed - b.volumeUsed);
    setInventory(sortedInventory);
  }, [recipies]);

  return {
    inventory,
  };
}
